import type { IAgentToolContext, IAgentTool } from '../../../../core/ai/agent/IAgentTool';

export const roleQueryTool: IAgentTool = {
  name: 'process_role_analysis',
  description: 'Analyze role assignments in the process. Shows unassigned steps, workload by role, and handoff points between roles. Use when the user asks about owners, roles, workload, or assignments.',
  parameters: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'What to analyze: "unassigned", "workload", or "handoffs"',
        enum: ['unassigned', 'workload', 'handoffs'],
        default: 'workload',
      },
    },
  },
  supportedDiagramTypes: ['flowchart'],
  tier: 'safe',
  category: 'query',

  async execute(params: Record<string, unknown>, context: IAgentToolContext) {
    const query = String(params.query ?? 'workload');
    const nodes = context.diagramData.nodes;
    const edges = context.diagramData.edges;

    const STEP_TYPES = new Set(['process', 'flowchart-shape', 'bpmn-shape', 'decision']);
    const steps = nodes.filter((n) => STEP_TYPES.has(n.type));

    const getAttr = (n: typeof nodes[0], key: string): string =>
      (n.attrs?.[key] ?? n.props?.[key] ?? '') as string;

    if (query === 'unassigned') {
      const unassigned = steps.filter((n) => !getAttr(n, 'owner'));
      if (unassigned.length === 0) return { success: true, summary: 'All steps have assigned owners.' };
      const names = unassigned.slice(0, 10).map((n) => getAttr(n, 'label') || getAttr(n, 'name') || n.id);
      return {
        success: true,
        summary: `${unassigned.length} unassigned step(s):\n${names.map((n) => `- ${n}`).join('\n')}${unassigned.length > 10 ? `\n...and ${unassigned.length - 10} more` : ''}`,
        data: { count: unassigned.length, nodeIds: unassigned.map((n) => n.id) },
      };
    }

    if (query === 'handoffs') {
      const nodeOwnerMap = new Map<string, string>();
      for (const step of steps) {
        const owner = getAttr(step, 'owner');
        if (owner) nodeOwnerMap.set(step.id, owner);
      }

      const handoffs: Array<{ from: string; to: string; atEdge: string }> = [];
      for (const edge of edges) {
        const fromOwner = nodeOwnerMap.get(edge.from);
        const toOwner = nodeOwnerMap.get(edge.to);
        if (fromOwner && toOwner && fromOwner !== toOwner) {
          handoffs.push({ from: fromOwner, to: toOwner, atEdge: edge.id });
        }
      }

      if (handoffs.length === 0) return { success: true, summary: 'No handoffs detected (single role or unassigned steps).' };

      const uniqueHandoffs = new Map<string, number>();
      for (const h of handoffs) {
        const key = `${h.from} → ${h.to}`;
        uniqueHandoffs.set(key, (uniqueHandoffs.get(key) ?? 0) + 1);
      }

      const lines = Array.from(uniqueHandoffs.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => `- ${key} (${count}x)`);

      return {
        success: true,
        summary: `${handoffs.length} handoff(s) between roles:\n${lines.join('\n')}`,
        data: { totalHandoffs: handoffs.length, unique: Object.fromEntries(uniqueHandoffs) },
      };
    }

    // Default: workload
    const byRole = new Map<string, { count: number; nodeIds: string[] }>();
    let unassignedCount = 0;

    for (const step of steps) {
      const owner = getAttr(step, 'owner');
      if (!owner) {
        unassignedCount++;
        continue;
      }
      const entry = byRole.get(owner) ?? { count: 0, nodeIds: [] };
      entry.count++;
      entry.nodeIds.push(step.id);
      byRole.set(owner, entry);
    }

    const sorted = Array.from(byRole.entries()).sort((a, b) => b[1].count - a[1].count);
    const lines = sorted.map(([role, { count }]) => `- ${role}: ${count} step(s)`);
    if (unassignedCount > 0) lines.push(`- (unassigned): ${unassignedCount} step(s)`);

    return {
      success: true,
      summary: `Workload by role:\n${lines.join('\n')}`,
      data: {
        roles: Object.fromEntries(sorted.map(([role, { count, nodeIds }]) => [role, { count, nodeIds }])),
        unassignedCount,
      },
    };
  },
};
